import React, { useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Grid from '@amzn/awsui-components-console/grid';
import Spinner from '@amzn/awsui-components-console/spinner';
import DataSourceOverview from './DataSourceOverview';
import DataSourceDetails from './DataSourceDetails';
import type { Collection } from '@amzn/aws-gcr-content-connection-management-client';
import type { ContentStats, DataSourceTab } from '../../types';
import './DataSourcesSection.scss';

export interface DataSourcesSectionProps {
  collections: Collection[];
  stats: ContentStats;
  loading: boolean;
  onCollectionClick: (id: string) => void;
}

const DataSourcesSection: React.FC<DataSourcesSectionProps> = ({ collections, stats, loading, onCollectionClick }) => {
  // Generate tabs from real data
  const tabs: DataSourceTab[] = React.useMemo(() => {
    const tabsMap = new Map<string, DataSourceTab>();

    collections.forEach((collection) => {
      const sourceId = collection.source?.toLowerCase() || 'unknown';
      const existing = tabsMap.get(sourceId);

      if (existing) {
        existing.collectionsCount++;
        existing.itemsCount += collection.itemCount || 0;
      } else {
        tabsMap.set(sourceId, {
          id: sourceId,
          label: collection.source || 'Unknown',
          collectionsCount: 1,
          itemsCount: collection.itemCount || 0,
        });
      }
    });

    return Array.from(tabsMap.values()).sort((a, b) => b.itemsCount - a.itemsCount);
  }, [collections]);

  const [activeTabId, setActiveTabId] = useState('');

  // Update activeTabId when tabs change
  React.useEffect(() => {
    if (tabs.length > 0 && !activeTabId) {
      setActiveTabId(tabs[0].id);
    }
  }, [tabs, activeTabId]);

  const filteredCollections = collections.filter((collection) => collection.source?.toLowerCase() === activeTabId);

  return (
    <div className="data-sources-section">
      <Box>
        <SpaceBetween size="l">
          <Box textAlign="center">
            <h2 className="section-title">Data Source</h2>
            <p className="section-subtitle">
              {loading ? (
                <>
                  <Spinner size="normal" /> Loading data...
                </>
              ) : (
                <>
                  With a total of {stats?.totalItems.toLocaleString() || '0'} content items across {stats?.totalSources || 0} data sources.
                </>
              )}
            </p>
          </Box>

          <div className="data-sources-grid">
            <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
              <DataSourceOverview stats={stats} collections={collections} loading={loading} />
              <DataSourceDetails
                tabs={tabs}
                activeTabId={activeTabId}
                onTabChange={setActiveTabId}
                collections={filteredCollections}
                loading={loading}
                onCollectionClick={onCollectionClick}
              />
            </Grid>
          </div>
        </SpaceBetween>
      </Box>
    </div>
  );
};

export default DataSourcesSection;
